import { apiFetch, ApiError } from './client'
import {
  activeResponseSchema,
  activeOrderSchema,
  dropshipDetailSchema,
  refreshSummarySchema,
  type ActiveResponse,
  type ActiveOrder,
  type DropshipDetail,
  type RefreshSummary,
} from '@/lib/schemas/active'

/**
 * Reads a { detail } error body in either shape the backend uses: a structured
 * object { code, message } or a plain string (auth / 5xx). Mirrors the dual-shape
 * handling in orderReview api — here we only need the human message.
 */
async function readErrorMessage(res: Response): Promise<string | undefined> {
  try {
    const body = (await res.json()) as { detail?: unknown }
    const detail = body?.detail
    if (typeof detail === 'string') return detail
    if (detail && typeof detail === 'object') {
      const message = (detail as Record<string, unknown>).message
      if (typeof message === 'string') return message
    }
  } catch {
    // non-JSON body — fall through to the caller's fallback
  }
  return undefined
}

async function fail(res: Response, fallback: string): Promise<never> {
  const detail = await readErrorMessage(res)
  throw new ApiError(res.status, detail ?? `${fallback} (${res.status})`, detail)
}

/** GET /api/active — submitted orders with status + tracking, newest first.
 * includeRetired also returns cancelled and completed orders. */
export async function getActive(includeRetired = false): Promise<ActiveResponse> {
  const path = includeRetired ? '/api/active?include_retired=true' : '/api/active'
  const res = await apiFetch(path, { method: 'GET' })
  if (!res.ok) return fail(res, 'Failed to load active dropships')
  return activeResponseSchema.parse(await res.json())
}

/** GET /api/dropship/{id} — the persisted submission detail for one dropship order. */
export async function getDropshipDetail(id: number): Promise<DropshipDetail> {
  const res = await apiFetch(`/api/dropship/${id}`, { method: 'GET' })
  if (!res.ok) return fail(res, 'Failed to load dropship order')
  return dropshipDetailSchema.parse(await res.json())
}

/** POST /api/dropship/refresh-tracking — poll KH now (all awaiting, or one order). */
export async function refreshTracking(id?: number): Promise<RefreshSummary> {
  const path =
    id == null ? '/api/dropship/refresh-tracking' : `/api/dropship/refresh-tracking?id=${id}`
  const res = await apiFetch(path, { method: 'POST' })
  if (!res.ok) return fail(res, 'Refresh failed')
  return refreshSummarySchema.parse(await res.json())
}

export interface ManualTrackingInput {
  tracking_number: string
  carrier: string
  post_to_shopify: boolean
}

/** POST /api/dropship/{id}/manual-tracking — record out-of-band tracking. */
export async function manualTracking(id: number, input: ManualTrackingInput): Promise<ActiveOrder> {
  const res = await apiFetch(`/api/dropship/${id}/manual-tracking`, {
    method: 'POST',
    body: JSON.stringify(input),
  })
  if (!res.ok) return fail(res, 'Failed to add tracking')
  return activeOrderSchema.parse(await res.json())
}

/** POST /api/dropship/{id}/mark-manual — KH received the order outside the API. */
export async function markManual(id: number, kh_reference: string): Promise<ActiveOrder> {
  const res = await apiFetch(`/api/dropship/${id}/mark-manual`, {
    method: 'POST',
    body: JSON.stringify({ kh_reference }),
  })
  if (!res.ok) return fail(res, 'Failed to mark as manually submitted')
  return activeOrderSchema.parse(await res.json())
}

/** POST /api/dropship/{id}/cancel — remove from active; order returns to the queue. */
export async function cancelDropship(id: number, reason: string): Promise<ActiveOrder> {
  const res = await apiFetch(`/api/dropship/${id}/cancel`, {
    method: 'POST',
    body: JSON.stringify({ reason }),
  })
  if (!res.ok) return fail(res, 'Failed to cancel')
  return activeOrderSchema.parse(await res.json())
}

/** POST /api/dropship/{id}/complete — retire a finished order from the Active board. */
export async function completeDropship(id: number): Promise<ActiveOrder> {
  const res = await apiFetch(`/api/dropship/${id}/complete`, { method: 'POST' })
  if (!res.ok) return fail(res, 'Failed to complete')
  return activeOrderSchema.parse(await res.json())
}

/** POST /api/dropship/{id}/repost-tracking — repost to the correct Shopify order. */
export async function repostTracking(id: number, shopify_order_id: string): Promise<ActiveOrder> {
  const res = await apiFetch(`/api/dropship/${id}/repost-tracking`, {
    method: 'POST',
    body: JSON.stringify({ shopify_order_id }),
  })
  if (!res.ok) return fail(res, 'Failed to re-post tracking')
  return activeOrderSchema.parse(await res.json())
}

export type Form5106Action = 'sent'

/** PATCH /api/dropship/{id}/form-5106 — advance the customs Form 5106 status. */
export async function updateForm5106(id: number, action: Form5106Action): Promise<ActiveOrder> {
  const res = await apiFetch(`/api/dropship/${id}/form-5106`, {
    method: 'PATCH',
    body: JSON.stringify({ action }),
  })
  if (!res.ok) return fail(res, 'Failed to update Form 5106')
  return activeOrderSchema.parse(await res.json())
}
